<template>
  <div class="mc-loads-table">
    <div class="clearfix">
      <div class="d-flex mc-list-header">
        <h4 class="card-title flex-grow-1">{{ $t('delivery.load') }}</h4>
        <McButton
          v-if="templateId && selectedIdsLength"
          variant="secondary-red"
          class="mx-2 ms-auto"
          icon="icon-outline_trash"
          :text="$t('global.delete')"
          @click="$refs['mc-modal-delete'].show()"
        />

        <McButton
          v-if="templateId"
          variant="primary"
          class="ms-auto"
          :text="$t('global.create')"
          icon="icon-fill_plus"
          :disabled="disabledGlobal"
          @click="handleAdd"
        />
      </div>
    </div>

    <b-card>
      <div v-if="!templateId" class="d-flex justify-content-center">
        <div class="d-flex w-auto gap-3">
          <div>
            <img :src="illustrationPath" alt />
          </div>
          <div class="d-flex flex-column justify-content-center gap-1">
            <strong class="primary-disabled-text">{{ $t('delivery.notLoad') }}</strong>
            <p class="secondary-disabled-text">
              {{ $t('global.action.save') }}
              <span class="blue-disabled-text">{{ $t('delivery.infoDelivery') }} </span
              >{{ $t('delivery.first') }}
            </p>
          </div>
        </div>
      </div>

      <div v-else>
        <b-row align-h="end">
          <b-col md="8" xl="10">
            <h6 class="mc-row-check mt-0" v-if="selectedIdsLength">
              <span class="badge btn-success">{{ selectedIdsLength }}</span>
              {{ $t('global.selectedRows') }}
              <a @click="handleUnselectAll" class="color-primary">{{
                $t('global.action.deselect')
              }}</a>
            </h6>
            <McAlert
              v-if="invalidKeysLength > 0 && selectedIdsLength === 0"
              variant="error"
              text="Terdapat data yang telah dihapus dari sistem."
            />
          </b-col>
          <b-col md="4" xl="2">
            <b-input-group size="sm" class="mc-has-search" @click="focusSearch">
              <template #prepend>
                <i class="ps-2 mc-icon icon-fill_search"></i>
              </template>
              <b-form-input
                :placeholder="`${$t('global.search')}...`"
                :disabled="disabledGlobal"
                :value="search"
                @input="handleSearchChange"
                ref="searchInput"
              ></b-form-input>
              <template #append v-if="search">
                <button
                  class="clear-search-btn"
                  :disabled="disabledGlobal"
                  @click="handleClearSearch"
                >
                  <i class="pe-2 mc-icon icon-outline_close"></i>
                </button>
              </template>
            </b-input-group>
          </b-col>
        </b-row>

        <FormulateForm
          v-model="formValues"
          name="editable-table-form"
          ref="formRef"
          :errors="formErrors"
          @submit="handleSubmit"
          @failed-validation="handleFailedValidation"
        >
          <div class="table-responsive border border-1 mc-loads-table__editable-table">
            <b-table
              :busy="isBusy"
              :items="items"
              :fields="computedFields"
              :per-page="limit"
              fixed
              show-empty
              class="editable-table"
            >
              <!-- Loading -->
              <template #table-busy>
                <div class="__busy">
                  <b-spinner class="align-middle mb-2"></b-spinner>
                  <strong>{{ $t('global.text.loading') }}...</strong>
                </div>
              </template>

              <!-- Empty Result -->
              <template #empty>
                <div class="text-center my-4">{{ $t('global.text.noData') }}</div>
              </template>

              <!-- Slot: Header Checkbox -->
              <template #head(checkbox)>
                <div class="form-check" style="width: 10px">
                  <b-form-checkbox
                    @change="handleSelectAll"
                    v-model="selectedAll"
                    :disabled="disabledGlobal"
                  ></b-form-checkbox>
                </div>
              </template>

              <!-- Slot: Header Data -->
              <template #head()="data">
                <div class="d-flex justify-content-between" @click="handleSortChange(data)">
                  {{ data.label }}
                  <template v-if="data.field.isSortable">
                    <div>
                      <div v-if="showSortIcon(data, 'asc')" class="sort-icon-up">
                        <i class="mc-icon icon-outline_chevron-up"></i>
                      </div>
                      <div v-if="showSortIcon(data, 'desc')" class="sort-icon-down">
                        <i class="mc-icon icon-outline_chevron-down"></i>
                      </div>
                    </div>
                  </template>
                </div>
              </template>

              <!-- Slot: Cell Checkbox -->
              <template #cell(checkbox)="data">
                <div class="form-check">
                  <b-form-checkbox
                    @change="(event) => handleSelect(event, data)"
                    :checked="selectedIds[data.item.id]"
                    :disabled="disabledGlobal"
                  ></b-form-checkbox>
                </div>
              </template>

              <!-- Slot: All Cells -->
              <template #cell()="{ index, field, value, item }">
                <template v-if="(field.editable || field.isEditable) && isEditingRow == index">
                  <ProductValueHelp
                    v-if="field.name == 'product_id'"
                    v-model="formValues[field.name]"
                    :field="field"
                    :fullObject.sync="fullObjects[field.name]"
                  />

                  <UnitSelectResourceful
                    v-else-if="field.name == 'uom'"
                    v-model="formValues[field.name]"
                    :field="field"
                    :fullObject.sync="fullObjects[field.name]"
                    :productObj="fullObjects.product_id || {}"
                  />

                  <FormulateInput
                    v-else
                    v-bind="{ ...field }"
                    :rowItem="item"
                    :label="null"
                    :name="field.name"
                    :type="field.type || 'text'"
                    :disabled="isSubmitting"
                    :help="field?.validation?.includes('required')"
                    class="p-0"
                    validation-behavior="live"
                    error-behavior="value"
                    size="sm"
                    :ref="`${field.name}-ref`"
                  />
                </template>

                <!-- Show Only Column -->
                <template v-else-if="!field.isEditable && isEditingRow == index">
                  <div>
                    {{ showOnlyValue(value, field, item) }}
                  </div>
                </template>

                <template v-else>
                  <div
                    class="td__overflow-ellipsis"
                    :class="isError(item, field) ? 'td__invalid-data' : undefined"
                  >
                    {{ transformCellValue(value, field, item) }}
                  </div>
                </template>
              </template>

              <!-- Slot: Action Column (Last Column) -->
              <template #cell(action)="data">
                <div v-if="isEditingRow == data.index">
                  <b-button variant="outline" class="__accept-button btn" type="submit">
                    <i class="mc-icon icon-fill_checkmark"></i>
                  </b-button>

                  <b-button
                    variant="outline"
                    class="__cancel-button btn"
                    @click="handleEditCancel(data)"
                  >
                    <i class="mc-icon icon-outline_close"></i>
                  </b-button>
                </div>

                <b-button
                  v-else
                  variant="outline"
                  :class="{
                    '__edit-button btn': true,
                    __disabled: isEditingRow !== null || isBusy,
                  }"
                  @click="handleEditStart(data)"
                >
                  <i class="mc-icon icon-outline_edit"></i>
                </b-button>
              </template>

              <template #custom-foot>
                <b-tr>
                  <b-td colspan="5">Total</b-td>
                  <b-td>{{ computedTotalWeights.toLocaleString() || '-' }} Kg</b-td>
                  <b-td>{{ computedTotalVolumes.toLocaleString() || '-' }} Cbm</b-td>
                  <b-td colspan="2"></b-td>
                </b-tr>
              </template>
            </b-table>
          </div>
        </FormulateForm>

        <div class="d-flex flex-wrap flex-md-nowrap">
          <div class="mc-limit">
            <div class="__limit">
              <div>{{ $t('global.show') }}</div>
              <b-form-select
                class="form-select form-select-sm __limit--dropdown"
                size="sm"
                :disabled="disabledGlobal"
                :options="limitOptions"
                :value="limit"
                @input="handleLimitChange"
              >
              </b-form-select>
              <div>{{ $t('global.object.data').toLowerCase() }}.</div>
            </div>

            <div class="__info" v-if="totalCount === 0">
              <strong>{{ totalCount }}</strong> {{ $t('global.object.of') }}
              <strong>{{ totalCount }}</strong> {{ $t('global.object.data').toLowerCase() }}.
            </div>

            <div class="__info" v-else>
              <strong>{{ startData }}</strong> - <strong>{{ endData }}</strong>
              {{ $t('global.object.of') }} <strong>{{ totalCount }}</strong>
              {{ $t('global.object.data').toLowerCase() }}.
            </div>
          </div>
          <div class="mc-pagination-container">
            <b-pagination
              :disabled="disabledGlobal"
              :per-page="limit"
              :total-rows="totalCount"
              :value="page"
              @change="handlePageChange"
            ></b-pagination>
          </div>
        </div>
      </div>

      <b-modal
        class="mc-modal"
        id="mc-modal-delete"
        ref="mc-modal-delete"
        :title="$t('global.action.delete')"
        size="md"
        centered
        scrollable
      >
        <div class="card-body p-2">
          <p>
            {{ $t('global.confirmDelete1') }} <strong>{{ selectedIdsLength }}</strong>
            {{ $t('global.confirmDelete2') }}
          </p>
        </div>
        <template #modal-footer="{ cancel }">
          <div class="d-flex">
            <McButton
              variant="secondary"
              class="me-2"
              :text="$t('global.action.cancel')"
              @click="cancel()"
            />
            <McButton variant="primary" :text="$t('global.action.delete')" @click="handleDelete" />
          </div>
        </template>
      </b-modal>
    </b-card>
  </div>
</template>

<script>
import { debounce } from '@/helpers/debounce'
import { sum, substract } from '@/helpers/calculate'

import ProductValueHelp from '../../wrapper/ProductValueHelp'
import UnitSelectResourceful from '../../wrapper/UnitSelectResourceful'

import McButton from '@/components/McButton'
import McAlert from '@/components/McAlert'

import { formatNumber } from '@/helpers/format-number'
import fields from '../../lib/fields/load-fields'
import { EventBus } from '../../lib/event-bus.js'

import { mapGetters } from 'vuex'

export default {
  components: {
    McButton,
    McAlert,
    ProductValueHelp,
    UnitSelectResourceful,
  },
  props: {
    apiClient: { required: true },
    totalWeights: { type: Number, default: 0 },
    totalVolumes: { type: Number, default: 0 },
    limitOptions: {
      default: () => [5, 10, 25, 50, 100],
    },
    invalidFields: { type: Array },
  },
  emits: ['update:totalWeights', 'update:totalVolumes'],
  data() {
    return {
      isBusy: true,
      isSubmitting: false,

      limit: this.limitOptions[0] ?? 10,
      page: 1,
      totalCount: 0,
      ids: [],
      items: [],
      sorts: [],
      selectedIds: {},
      selectedIdsLength: 0,
      selectedAll: false,
      isEditingRow: null,
      search: '',
      formValues: {},
      fullObjects: {},

      loadsData: {},
      showResponse: {},
      invalidItems: {},
      formErrors: {},
      fields,
    }
  },
  computed: {
    templateId() {
      return this.$route.params.id
    },
    disabledGlobal() {
      return this.isEditingRow !== null || this.isBusy
    },
    startData() {
      return this.limit * this.page - this.limit + 1
    },
    endData() {
      const _endData = this.limit * this.page
      return this.totalCount < _endData ? this.totalCount : _endData
    },
    computedFields() {
      return [
        {
          key: 'checkbox',
          label: '',
          thStyle: 'width: 25px',
        },
        ...this.fields,
        {
          key: 'action',
          label: '',
          thClass: 'mc-action-col-head',
          tdClass: 'mc-action-cell',
        },
      ]
    },
    computedTotalWeights() {
      let totalWeights = this.totalWeights

      // if it's in editing mode
      if (this.isEditingRow != null) {
        if (this.totalCount > 0) {
          totalWeights = substract(totalWeights, this.showResponse.total_weight)
        }

        const uomObjWeight = this.fullObjects?.uom?.weight * this.formValues.quantity || 0
        totalWeights = sum(totalWeights || 0, uomObjWeight)
      }

      return totalWeights
    },
    computedTotalVolumes() {
      let totalVolumes = this.totalVolumes

      // if it's in editing mode
      if (this.isEditingRow != null) {
        if (this.totalCount > 0) {
          totalVolumes = substract(totalVolumes, this.showResponse.total_volume)
        }

        const uomObjVolume = this.fullObjects?.uom?.volume * this.formValues.quantity || 0
        totalVolumes = sum(totalVolumes || 0, uomObjVolume)
      }

      return totalVolumes
    },
    invalidKeysLength() {
      return Object.keys(this.invalidItems).length
    },
    ...mapGetters({
      isWhitelabel: 'identity/isWhitelabel',
      isMetrack: 'identity/isMetrack',
    }),

    illustrationPath() {
      if (this.isWhitelabel() || this.isMetrack()) {
        return require(`@/assets/images/illustration/whitelabel/no-data.svg`)
      }
      return require(`@/assets/images/illustration/saas/no-data.svg`)
    },
  },
  watch: {
    async formValues() {
      const keys = Object.keys(this.formErrors)

      if (this.isEditingRow === null && keys === 0) return

      keys.forEach((key) => {
        if (this.formValues[key] === this.loadsData[key]) return

        this.$delete(this.formErrors, key)
      })
    },
  },
  async mounted() {
    if (!this.templateId) return

    await this.$nextTick()
    await this.getData({
      limit: this.limit,
      page: this.page,
    })
  },
  methods: {
    focusSearch() {
      this.$refs.searchInput.focus()
    },
    // eslint-disable-next-line no-unused-vars
    handleFailedValidation(errors) {
      return this.$mcToast.error('Terdapat kesalahan format pada input anda')
    },
    showOnlyValue(value, field) {
      if (!this.formValues.product_id) return `- ${field.afterText ?? ''}`

      if (field.name === 'product_type') {
        const productObj = this.fullObjects.product_id

        return productObj.product_type.name
      }

      const uomObj = this.fullObjects.uom
      const key = field.name.replace('total_', '')

      const newValue = formatNumber(uomObj?.[key] * this.formValues.quantity)

      return `${newValue || '-'} ${field.afterText ?? ''}`
    },
    transformCellValue(value, field, item) {
      // Show master data error
      if (item.product.is_error) {
        if (field.name === 'product_id') {
          return 'Data dihapus'
        }

        return ''
      }

      if (value.is_error) {
        return 'Data dihapus'
      }

      if (value === false) return `- ${field.afterText ?? ''}`
      if (typeof value === 'number') return `${value.toLocaleString()} ${field.afterText ?? ''}`

      if (field.name === 'uom') {
        return value[field.trackBy]
      }

      return value
    },
    transformFields(formValues, fields) {
      const formValuesKeys = Object.keys(formValues)
      const validKeys = formValuesKeys.filter((key) => formValues[key] !== false)

      return Object.assign(
        {},
        ...validKeys.map((key) => {
          const currentField = fields.find((item) => item.key == key)
          const value = formValues[key]

          if (currentField && currentField._type === 'number') {
            const isValid = Boolean(value) || value === '0' || value === 0
            return { [key]: isValid ? Number(value) : null }
          } else if (currentField && currentField.type === 'text') {
            if (!formValues[key]) return { [key]: '' }
            return { [key]: formValues[key].trim().replace(/  +/g, ' ') }
          }

          return { [key]: value }
        })
      )
    },
    isError(item, field) {
      if (!item.product.is_error && !item.uom.is_error) {
        return false
      }

      if (field.name === 'uom') {
        return true
      }

      if (field.name === 'product_id' && item.product.is_error) {
        return true
      }

      return false
    },
    assignErrors() {
      const indexObject = {}

      this.items.forEach((item) => {
        const loadObj = {}
        if (item.product.is_error) {
          loadObj.product_id = ['Data telah dihapus dari sistem.']
        }

        if (item.uom.is_error) {
          loadObj.uom = ['Data telah dihapus dari sistem.']
        }

        if (Object.keys(loadObj).length === 0) return

        indexObject[item.id] = loadObj
      })

      this.invalidItems = indexObject
    },
    async getData(opts) {
      if (!this.apiClient?.get) {
        this.isBusy = false
        return
      }

      this.isBusy = true
      const res = await this.apiClient.get(opts)
      if (!res) return
      this.items = res.data.paginated_result
      this.ids = res.data.ids
      this.page = res.metadata.page
      this.totalCount = res.metadata.total_count

      this.assignErrors()

      this.isBusy = false
    },
    handleSearchChange: debounce(async function (_search) {
      this.search = _search
      const opts = {
        limit: this.limit,
        search: _search != '' ? _search : undefined,
        page: 1,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      }
      await this.getData(opts)
      this.$refs['search-input-ref'].$el.focus()
    }, 500),
    handleClearSearch() {
      this.handleSearchChange(undefined)
    },
    async handleLimitChange(_limit) {
      this.limit = _limit

      await this.getData({
        limit: _limit,
        search: this.search != '' ? this.search : undefined,
        page: 1,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      })
    },
    async handlePageChange(newPage) {
      const opts = {
        page: newPage,
        search: this.search != '' ? this.search : undefined,
        limit: this.limit,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      }
      await this.getData(opts)
    },
    showSortIcon(data, order) {
      const currentColumnSort = this.sorts.find((sort) => sort.key === data.field.key)
      if (!currentColumnSort) {
        return true
      }

      return currentColumnSort.key === data.field.key && currentColumnSort.order === order
    },
    async handleSortChange(data) {
      // If this function is called without data params,
      // remove sorting (revert to default)
      if (!data) {
        this.sorts = []
        return
      }

      // If field not sortable or If currently editing a field, disable sort
      if (!data?.field?.isSortable) return
      if (this.isEditingRow !== null) return

      // Get index of current column sort, if any
      const currentColumnSortIndex = this.sorts.findIndex((sort) => sort.key === data.field.key)
      // Get the sorting data (key, order)
      const currentColumnSort = this.sorts[currentColumnSortIndex]
      const newSorts = [...this.sorts]
      // This switch case is used to toggle between these conditions:
      // default - asc - desc - default (repeat)
      switch (currentColumnSort?.order) {
        case 'asc':
          newSorts[currentColumnSortIndex] = {
            key: currentColumnSort.key,
            order: 'desc',
          }
          break

        case 'desc':
          newSorts.splice(currentColumnSortIndex, 1)
          break

        default:
          newSorts.push({ key: data.field.key, order: 'asc' })
          break
      }

      this.sorts = [...newSorts]
      await this.$nextTick()

      await this.getData({
        limit: this.limit,
        page: 1,
        search: this.search != '' ? this.search : undefined,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      })
    },
    async handleEditStart(data) {
      if (this.selectedIdsLength) this.handleUnselectAll()
      if (this.isEditingRow != null) return

      // refetch single data from backend for data integrity
      const refetchedData = await this.apiClient.show(data.item.id)
      if (!refetchedData) return

      const loadData = refetchedData.data

      // assign formValues with response show data
      this.fields.forEach((field) => {
        if (!field.isEditable) return
        let value

        value = loadData[field.name]

        // assign for the product_id
        if (field.name === 'product_id') {
          if (loadData.product.is_error) {
            value = ' '
          } else {
            value = loadData.product.id
          }

          this.$set(this.fullObjects, field.name, loadData.product)
        } else if (field.name === 'uom') {
          const uomObj = {}

          if (loadData.product.is_error || loadData.uom.is_error) {
            value = ' '
          } else {
            value = loadData.uom.name

            uomObj.id = ''
            uomObj.name = value
            uomObj.weight = loadData.total_weight / loadData.quantity
            uomObj.total_weight = loadData.total_total_weight / loadData.quantity
          }

          this.$set(this.fullObjects, field.name, uomObj)
        }

        this.$set(this.formValues, field.name, value)
      })

      // update the refetched data (row) in the table
      // map is used since 'this.computedItems[index] = {}' will not work
      this.items = this.items.map((item) => {
        if (item.id == data.item.id) return loadData
        return item
      })

      this.isEditingRow = data.index
      this.showResponse = { ...loadData }
      this.loadsData = { ...this.formValues }

      // assign the form errors from invalidItems with the current id
      if (this.invalidItems[loadData.id]) {
        this.formErrors = { ...this.invalidItems[loadData.id] }
      }

      await this.$nextTick()
    },
    handleEditCancel(data) {
      if (this.isSubmitting) return
      if (this.isAdding) {
        const [, ...newItems] = this.items
        this.items = newItems
        this.isAdding = false
      } else {
        // assign showResponse to items if cancel edit
        const newItems = [...this.items]
        newItems.splice(data.index, 1, this.showResponse)
        this.items = newItems
      }

      this.formValues = {}
      this.fullObjects = {}
      this.formErrors = {}
      this.isEditingRow = null
    },
    async submitStore(data) {
      const createdData = await this.apiClient.store(data)

      if (createdData.errors) {
        return { errors: createdData.errors }
      }
      if (!createdData) return false

      const refetchedData = await this.apiClient.show(createdData.data.id)
      if (!refetchedData) return false

      this.ids.push(refetchedData.data.id)
      this.totalCount += 1

      this.$mcToast.success('Data berhasil ditambahkan')

      return {
        ...data,
        ...refetchedData.data,
      }
    },
    async submitUpdate(data, oldData) {
      const [patchData, hasChanged] = this.filterUnchangedData(data, this.loadsData)
      if (!hasChanged) {
        this.isSubmitting = false
        this.handleEditCancel({ index: this.isEditingRow })
        return
      }

      const updatedData = await this.apiClient.update(oldData.id, patchData)
      if (updatedData.errors) {
        return { errors: updatedData.errors }
      }
      if (!updatedData) return false

      const refetchedData = await this.apiClient.show(oldData.id)
      if (!refetchedData) return false

      if (this.invalidItems[oldData.id]) {
        this.$delete(this.invalidItems, oldData.id)
        this.formErrors = {}
      }

      this.$mcToast.success('Perubahan data berhasil disimpan')

      return {
        ...refetchedData.data,
      }
    },
    async handleSubmit(data) {
      if (this.isSubmitting) {
        this.$mcToast.warning('Mohon tunggu')
        return
      }

      if (Object.keys(this.formErrors).length) {
        this.$mcToast.error('Terdapat kesalahan format pada input anda')
        return
      }

      this.isSubmitting = true

      const transformedData = this.transformFields(data, this.fields)

      let newData = null

      if (this.isAdding) {
        newData = await this.submitStore(transformedData)
      } else {
        newData = await this.submitUpdate(transformedData, this.items[this.isEditingRow])
      }

      // if there is any error master data
      if (newData.errors) {
        for (const [key, value] of Object.entries(newData.errors)) {
          this.$set(this.formErrors, key, value)
        }

        this.$mcToast.error('Terdapat kesalahan format pada input anda')
        this.isSubmitting = false
        return
      }

      // If newData is false, it means there is an error
      if (newData === false) {
        this.isSubmitting = false
        return
      }

      // If newData is null, it means there is no change
      // Replace data on the table
      if (newData) {
        this.items.splice(this.isEditingRow, 1, newData)
      }

      this.$emit('update:totalWeights', this.computedTotalWeights)
      this.$emit('update:totalVolumes', this.computedTotalVolumes)

      this.formErrors = {}
      this.formValues = {}
      this.fullObjects = {}
      this.showResponse = {}

      this.isEditingRow = null
      this.isAdding = false
      this.isSubmitting = false
    },
    filterUnchangedData(newData, oldData) {
      const keys = new Set([...Object.keys(newData)])
      const _newValue = {}
      keys.forEach((key) => {
        if (newData[key] == oldData[key]) return
        _newValue[key] = newData[key]
      })

      const entries = Object.entries(_newValue)
      const hasChanged = entries.length > 0 && entries.every((value) => value)
      return [_newValue, hasChanged]
    },
    async handleDelete() {
      const promises = []
      for (let key in this.selectedIds) {
        if (this.selectedIds[key]) {
          promises.push(this.apiClient.delete(key))
        }
      }
      await Promise.all(promises)
      if (this.search) this.handleClearSearch()
      await this.getData({
        limit: this.limit,
        page: 1,
      })

      for (const selectedid in this.selectedIds) {
        if (this.invalidItems[selectedid]) {
          this.$delete(this.invalidItems, selectedid)
        }
      }

      await this.$nextTick()
      this.handleSortChange()
      this.handleUnselectAll()
      this.$refs['mc-modal-delete'].hide()
      this.$mcToast.success('Data berhasil dihapus')

      EventBus.$emit('show-template-data')
    },
    handleSelect(checked, data) {
      this.selectedIds = { ...this.selectedIds, [data.item.id]: checked }
      if (checked) {
        this.selectedIdsLength += 1
        if (this.selectedIdsLength == this.totalCount) this.selectedAll = true
      } else {
        this.selectedAll = false
        this.selectedIdsLength -= 1
      }
    },
    handleSelectAll(event) {
      if (event) {
        this.selectedIds = Object.fromEntries(this.ids.map((key) => [key, true]))
        this.selectedIdsLength = this.totalCount
        this.selectedAll = true
      } else {
        this.handleUnselectAll()
      }
    },
    handleUnselectAll() {
      this.selectedIds = {}
      this.selectedAll = false
      this.selectedIdsLength = 0
    },
    handleAdd() {
      if (this.isEditingRow != null) return
      this.isEditingRow = 0
      this.isAdding = true

      this.items = [{ id: 0 }, ...this.items]
    },
  },
}
</script>

<style lang="scss">
@import './style.scss';
</style>
